import { tabAccount } from './tab-account';
import { errorMessage as launchErrorMessage } from './i18n/errors';

/**
 * The one web API client for products that keep one account per tab (D-028), F-26 C6 and M5. A
 * product makes its client once (`createApi({ app: 'contacts', product: 'Dugzi Contacts' })`) and its
 * `lib/api.ts` re-exports `api`, `withAccount` and `TAB`. Accounts keeps its own client: it is the
 * sign-in itself (field errors, no sign-in redirect). The options below exist so each product keeps
 * the exact behaviour its own client had; a new product needs none of them.
 */

// Declared fields, not parameter properties: the unit tests run under Node's type stripping, which
// accepts only erasable TypeScript (no parameter properties, enums or namespaces).
export class ApiError extends Error {
  code: string;
  status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

export type ApiOptions = {
  method?: string;
  body?: unknown;
  /** Bytes sent as they are (uploads, imports). POST unless `method` says otherwise. */
  rawBody?: BodyInit;
  /** The raw body's type: `application/octet-stream` unless given; `null` lets the browser choose (a form's boundary, a file's own type). */
  contentType?: string | null;
  /** Extra request headers (a share link's grant, `If-Match`, a file name). */
  headers?: Record<string, string>;
  /** A call made without the person's sign-in (Drive's public links): no cookies, no account, and a 401 is an ordinary error. */
  anonymous?: boolean;
  signal?: AbortSignal;
};

/** A product's offline copy: `get` answers a GET that could not reach the server; `put` keeps a GET's answer. */
export type OfflineHook = {
  get(path: string): Promise<unknown> | unknown;
  put(path: string, data: unknown): Promise<unknown> | unknown;
};

export type ApiConfig = {
  /** The product's id: the tab's account lives under `dz.<app>.account` (see `tabAccount`). */
  app: string;
  /** The name in the offline message ("You're offline, or <product> can't be reached right now."). */
  product: string;
  /** Answer GETs from, and save them to, the product's offline copy (Mail). */
  offline?: OfflineHook;
  /** Send `X-Dugzi-Tab` (so live updates skip this tab's own changes). Default true; Mail has never sent it. */
  tabHeader?: boolean;
  /** What a 204 answers: `undefined` (default), `{}` as Mail's client always has (it parsed the empty body), or `null` (Calendar). */
  noContent?: 'undefined' | 'object' | 'null';
  /** The fetch cache mode for every call (Tasks: `no-store`). Default: the browser's. */
  cache?: RequestCache;
  /** What a 401 does: `signin` (default) sends the person to sign in; `error` throws it like any other error (Search and Ask Dugzi never sign in by themselves). */
  unauthenticated?: 'signin' | 'error';
  /** A 401 that needs a sign-in, before the default redirect: return an error when the product signed in its own way (Calendar inside Mail, D-065). */
  onSignIn?: () => ApiError | undefined;
  /** The words for a failed call when the server sends none. */
  fallback?: { message: string; code: string };
  /** Rewrites the server's error message (Calendar translates its feed errors). */
  errorMessage?: (message: string | undefined, code: string | undefined) => string | undefined;
};

export type Api = {
  api<T = any>(path: string, opts?: ApiOptions): Promise<T>;
  /** Adds this tab's account to a URL the browser loads by itself (downloads, the live stream). */
  withAccount(url: string): string;
  /** This browser tab, so live updates skip the changes it made itself. */
  TAB: string;
};

const FALLBACK = { message: 'Something went wrong on our side. Please try again.', code: 'unknown' };

/** Where a 401 sends the person: sign in, then back to where they were. */
function signIn() {
  window.location.assign(`/api/auth/login?return_to=${encodeURIComponent(location.pathname + location.search)}`);
}

function requestHeaders(config: ApiConfig, TAB: string, opts: ApiOptions): Record<string, string> {
  const headers: Record<string, string> = {};
  if (config.tabHeader ?? true) headers['X-Dugzi-Tab'] = TAB;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.rawBody !== undefined) {
    const type = opts.contentType === undefined ? 'application/octet-stream' : opts.contentType;
    if (type !== null) headers['Content-Type'] = type;
  }
  Object.assign(headers, opts.headers);
  const account = opts.anonymous ? null : tabAccount.get(config.app);
  if (account) headers['X-Dugzi-Account'] = account;
  return headers;
}

export function createApi(config: ApiConfig): Api {
  const { app, product, offline } = config;
  const signsIn = (config.unauthenticated ?? 'signin') === 'signin';
  const fallback = config.fallback ?? FALLBACK;
  const TAB = Math.random().toString(36).slice(2, 12);

  function withAccount(url: string): string {
    const a = tabAccount.get(app);
    if (!a) return url;
    return url + (url.includes('?') ? '&' : '?') + 'account=' + encodeURIComponent(a);
  }

  /** JSON API client. A 401 sends the person to sign in and brings them back to where they were. */
  async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
    const method = opts.method ?? (opts.body !== undefined || opts.rawBody !== undefined ? 'POST' : 'GET');
    let res: Response;
    try {
      res = await fetch(path, {
        method,
        headers: requestHeaders(config, TAB, opts),
        body: opts.rawBody ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
        signal: opts.signal,
        credentials: opts.anonymous ? 'omit' : 'same-origin',
        cache: config.cache,
      });
    } catch {
      opts.signal?.throwIfAborted();
      if (offline && method === 'GET') {
        const hit = await Promise.resolve().then(() => offline.get(path)).catch(() => undefined);
        if (hit !== undefined) return hit as T;
      }
      throw new ApiError(`You're offline, or ${product} can't be reached right now.`, 'network', 0);
    }
    if (res.status === 401 && signsIn && !opts.anonymous) {
      const data = await res.json().catch(() => ({}));
      // This tab's account was signed out elsewhere: fall back to another signed-in account.
      if (data?.error?.code === 'account_signed_out') {
        tabAccount.set(app, null);
        window.location.reload();
        throw new ApiError('Switching account…', 'account_signed_out', 401);
      }
      const own = config.onSignIn?.();
      if (own) throw own;
      signIn();
      throw new ApiError('Signing you in…', 'unauthenticated', 401);
    }
    if (res.status === 204) {
      const noContent = config.noContent ?? 'undefined';
      if (noContent === 'undefined') return undefined as T;
      if (noContent === 'null') return null as T;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // D-296 launch refusals (sharing_off, product_unavailable) read the same in every product.
      const raw = launchErrorMessage(data?.error ?? {}, '') || undefined;
      const message = config.errorMessage ? config.errorMessage(raw, data?.error?.code) : raw;
      throw new ApiError(message ?? fallback.message, data?.error?.code ?? fallback.code, res.status);
    }
    if (offline && method === 'GET') void Promise.resolve().then(() => offline.put(path, data)).catch(() => {});
    return data as T;
  }

  return { api, withAccount, TAB };
}
